import React from "react";
import { Icon } from "@iconify/react";

/**
 * @typedef {'eventForm' | 'registration' | 'filters' | 'display'} DepartmentContext
 * @typedef {'named' | 'other'} StatsGroup
 *
 * @typedef {Object} Department
 * @property {string} [_id] - MongoDB ObjectId when loaded from API
 * @property {string} name
 * @property {string} icon - Iconify icon id (not pre-rendered JSX)
 * @property {string} color - Filter UI accent color
 * @property {boolean} showInEventForm
 * @property {boolean} showInRegistration
 * @property {boolean} showInFilters
 * @property {StatsGroup} statsGroup - "named" for individual stats buckets, "other" rolls up
 * @property {string} [statsKey] - Sardine stats property key (named departments only)
 * @property {boolean} hasHeadOfRole - Whether a "Head of …" position exists
 * @property {string | null} [coverImage] - Optional cover image URL
 * @property {{ _id?: string, name?: string } | string | null} [headRole] - RBAC role used to resolve the department head
 */

/** @typedef {string | Department} DepartmentRef */

/** Canonical ordered department list — single source of truth for names, icons, colors, and visibility flags. */
export const DEPARTMENTS = /** @type {Department[]} */ ([
  {
    name: "Parties & Entertainment",
    icon: "lucide:party-popper",
    color: "#ec4899",
    showInEventForm: true,
    showInRegistration: true,
    showInFilters: true,
    statsGroup: "named",
    statsKey: "PARTYEvents",
    hasHeadOfRole: true,
  },
  {
    name: "Integration & Cultural Events",
    icon: "carbon:friendship",
    color: "#06b6d4",
    showInEventForm: true,
    showInRegistration: true,
    showInFilters: true,
    statsGroup: "named",
    statsKey: "ICEEvents",
    hasHeadOfRole: true,
  },
  {
    name: "Sports & Trips",
    icon: "fluent:sport-16-regular",
    color: "#10b981",
    showInEventForm: true,
    showInRegistration: true,
    showInFilters: true,
    statsGroup: "named",
    statsKey: "STRIPSEvents",
    hasHeadOfRole: true,
  },
  {
    name: "Projects & Causes",
    icon: "ph:hand-heart",
    color: "#f97316",
    showInEventForm: true,
    showInRegistration: true,
    showInFilters: true,
    statsGroup: "named",
    statsKey: "PCEvents",
    hasHeadOfRole: true,
  },
  {
    name: "Education",
    icon: "tabler:school",
    color: "#84cc16",
    showInEventForm: true,
    showInRegistration: true,
    showInFilters: true,
    statsGroup: "named",
    statsKey: "EDUEvents",
    hasHeadOfRole: true,
  },
  {
    name: "Human Resources",
    icon: "material-symbols:digital-wellbeing",
    color: "#6b7280",
    showInEventForm: true,
    showInRegistration: true,
    showInFilters: true,
    statsGroup: "named",
    statsKey: "HREvents",
    hasHeadOfRole: true,
  },
  {
    name: "Partnerships",
    icon: "ph:handshake-fill",
    color: "#f59e0b",
    showInEventForm: true,
    showInRegistration: true,
    showInFilters: true,
    statsGroup: "other",
    hasHeadOfRole: true,
  },
  {
    name: "Communication",
    icon: "lucide:messages-square",
    color: "#ef4444",
    showInEventForm: true,
    showInRegistration: true,
    showInFilters: true,
    statsGroup: "other",
    hasHeadOfRole: true,
  },
  {
    name: "Marketing",
    icon: "lucide:megaphone",
    color: "#a855f7",
    showInEventForm: true,
    showInRegistration: true,
    showInFilters: true,
    statsGroup: "other",
    hasHeadOfRole: true,
  },
  {
    name: "IT",
    icon: "icon-park-outline:click",
    color: "#06b6d4",
    showInEventForm: true,
    showInRegistration: true,
    showInFilters: true,
    statsGroup: "other",
    hasHeadOfRole: true,
  },
  {
    name: "Board",
    icon: "lucide:landmark",
    color: "#6366f1",
    showInEventForm: false,
    showInRegistration: false,
    showInFilters: true,
    statsGroup: "other",
    hasHeadOfRole: true,
  },
]);

/** Ordered list of all department names. */
export const DEPARTMENT_NAMES = DEPARTMENTS.map((d) => d.name);

/**
 * Resolve a department catalog — API list when available, static fallback otherwise.
 * @param {Department[] | undefined | null} catalog
 * @returns {Department[]}
 */
export function getDepartmentCatalog(catalog) {
  return catalog?.length ? catalog : DEPARTMENTS;
}

/**
 * Normalize a department reference (API object or legacy name string) to a catalog entry.
 * @param {DepartmentRef | null | undefined} dept
 * @param {Department[] | undefined | null} [catalog]
 * @returns {Department | undefined}
 */
export function resolveDepartmentRef(dept, catalog) {
  if (!dept) return undefined;
  const list = getDepartmentCatalog(catalog);

  if (typeof dept === "object") {
    const byId = dept._id
      ? list.find((d) => String(d._id) === String(dept._id))
      : undefined;
    if (byId) {
      return { ...byId, ...dept, name: dept.name || byId.name };
    }
    const byName = dept.name
      ? list.find((d) => d.name === dept.name)
      : undefined;
    return byName ? { ...byName, ...dept } : dept;
  }

  if (typeof dept === "string") {
    const byId = list.find((d) => d._id && String(d._id) === dept);
    if (byId) return byId;
    return list.find((d) => d.name === dept) || getDepartmentByName(dept);
  }

  return undefined;
}

/**
 * @param {DepartmentRef | null | undefined} dept
 * @returns {string | undefined}
 */
export function getDepartmentName(dept, catalog) {
  const resolved = resolveDepartmentRef(dept, catalog);
  return resolved?.name;
}

/**
 * @param {DepartmentRef | null | undefined} dept
 * @returns {string | undefined}
 */
export function getDepartmentId(dept) {
  if (!dept || typeof dept !== "object" || !dept._id) return undefined;
  return String(dept._id);
}

/**
 * Extract department ObjectId strings from API objects or legacy string names.
 * @param {DepartmentRef[] | undefined | null} departments
 * @param {Department[] | undefined | null} [catalog]
 * @returns {string[]}
 */
export function extractDepartmentIds(departments, catalog) {
  if (!departments?.length) return [];
  const list = getDepartmentCatalog(catalog);

  return departments.map((dept) => {
    if (typeof dept === "object" && dept._id) {
      return String(dept._id);
    }
    const name = getDepartmentName(dept);
    const match = list.find((d) => d.name === name);
    return match?._id ? String(match._id) : name;
  });
}

/**
 * Match a department ref against a filter value (id or legacy name).
 * @param {DepartmentRef | null | undefined} dept
 * @param {string} filterValue
 * @returns {boolean}
 */
export function departmentMatchesFilter(dept, filterValue) {
  if (!dept || !filterValue) return false;
  const deptId = getDepartmentId(dept);
  const deptName = getDepartmentName(dept);
  return (
    (deptId && String(deptId) === String(filterValue)) ||
    deptName === filterValue ||
    dept === filterValue
  );
}

/**
 * @param {DepartmentRef[] | undefined | null} userDepartments
 * @param {string} filterValue
 * @returns {boolean}
 */
export function userHasDepartment(userDepartments, filterValue) {
  return userDepartments?.some((dept) => departmentMatchesFilter(dept, filterValue)) ?? false;
}

/**
 * Normalize URL/filter tokens to department ids when possible.
 * @param {string[]} values
 * @param {Department[] | undefined | null} [catalog]
 * @returns {string[]}
 */
export function resolveDepartmentFilterValues(values, catalog) {
  const list = getDepartmentCatalog(catalog);
  return values.map((value) => {
    const byId = list.find((d) => d._id && String(d._id) === value);
    if (byId) return String(byId._id);
    const byName = list.find((d) => d.name === value);
    if (byName?._id) return String(byName._id);
    return value;
  });
}

const CONTEXT_FLAG = {
  eventForm: "showInEventForm",
  registration: "showInRegistration",
  filters: "showInFilters",
};

/** Board-level positions that are not derived from departments. */
export const BOARD_POSITIONS = [
  "Events Manager",
  "President",
  "Secretary",
  "Treasurer",
  "Vice President",
  "MAG - Main Chair",
  "MAG - Vice Chair",
  "MAG - Technical Chair",
  "CF - Main Auditor",
  "CF - Reporter Auditor",
  "CF - Secretary Auditor",
];

/**
 * Look up a department by its display name.
 * @param {string} name
 * @returns {Department | undefined}
 */
export function getDepartmentByName(name) {
  return DEPARTMENTS.find((d) => d.name === name);
}

/**
 * Return the Iconify icon id for a department.
 * @param {DepartmentRef} dept
 * @param {Department[] | undefined | null} [catalog]
 * @returns {string | undefined}
 */
export function getDepartmentIconId(dept, catalog) {
  const resolved = resolveDepartmentRef(dept, catalog);
  return resolved?.icon;
}

/**
 * Render an Iconify icon for a department.
 * @param {DepartmentRef} dept
 * @param {Object} [props] - Props forwarded to `<Icon />`
 * @param {Department[] | undefined | null} [catalog]
 * @returns {React.ReactElement | null}
 */
export function getDepartmentIcon(dept, props = {}, catalog) {
  const iconId = getDepartmentIconId(dept, catalog);
  if (!iconId) return null;
  return <Icon icon={iconId} {...props} />;
}

/**
 * Return the filter UI color for a department.
 * @param {DepartmentRef} dept
 * @param {Department[] | undefined | null} [catalog]
 * @returns {string | undefined}
 */
export function getDepartmentColor(dept, catalog) {
  const resolved = resolveDepartmentRef(dept, catalog);
  return resolved?.color;
}

/**
 * Return departments visible in a given UI context.
 * @param {DepartmentContext} context
 * @param {Department[] | undefined | null} [catalog]
 * @returns {Department[]}
 */
export function getDepartmentsForContext(context, catalog) {
  const list = getDepartmentCatalog(catalog);

  if (context === "display") {
    return [...list];
  }

  const flag = CONTEXT_FLAG[context];
  if (!flag) {
    return [];
  }

  return list.filter((d) => d[flag]);
}

/**
 * Stats/chart grouping: named departments get individual buckets; back-office depts roll into "Other".
 * @param {Department[] | undefined | null} [catalog]
 * @returns {{
 *   named: Department[],
 *   otherLabel: string,
 *   otherStatsKey: string,
 *   chartLabels: string[],
 *   getStatsKeyForDepartment: (departmentRef: DepartmentRef) => string,
 * }}
 */
export function getStatsDepartments(catalog) {
  const list = getDepartmentCatalog(catalog);
  const named = list.filter((d) => d.statsGroup === "named");

  return {
    named,
    otherLabel: "Other",
    otherStatsKey: "OtherEvents",
    chartLabels: [...named.map((d) => d.name), "Other"],
    getStatsKeyForDepartment(departmentRef) {
      const dept = resolveDepartmentRef(departmentRef, list);
      if (dept?.statsGroup === "named" && dept.statsKey) {
        return dept.statsKey;
      }
      return "OtherEvents";
    },
  };
}

/**
 * Return the RBAC role name for a department head, if the department has one.
 * @param {DepartmentRef | null | undefined} dept
 * @param {Department[] | undefined | null} [catalog]
 * @returns {string | null}
 */
export function getDepartmentHeadRoleName(dept, catalog) {
  const resolved = resolveDepartmentRef(dept, catalog);
  if (!resolved) return null;

  if (resolved.headRole && typeof resolved.headRole === "object" && resolved.headRole.name) {
    return resolved.headRole.name;
  }

  if (!resolved.hasHeadOfRole) return null;
  return `Head of ${resolved.name}`;
}

/**
 * Resolve the stored head role id for a department, if any.
 * @param {DepartmentRef | null | undefined} dept
 * @param {Department[] | undefined | null} [catalog]
 * @param {{ name: string, _id: string }[] | undefined | null} [roles]
 * @returns {string | null}
 */
export function getDepartmentHeadRoleId(dept, catalog, roles) {
  const resolved = resolveDepartmentRef(dept, catalog);
  if (!resolved) return null;

  if (resolved.headRole) {
    if (typeof resolved.headRole === "object" && resolved.headRole._id) {
      return String(resolved.headRole._id);
    }
    return String(resolved.headRole);
  }

  const fallbackRoleName = getDepartmentHeadRoleName(resolved, catalog);
  if (!fallbackRoleName || !roles?.length) return null;

  const match = roles.find((role) => role.name === fallbackRoleName);
  return match?._id ? String(match._id) : null;
}

/**
 * Derive "Head of …" options from department config plus board-level roles.
 * @param {Department[] | undefined | null} [catalog]
 * @returns {string[]}
 */
export function getHeadOfPositions(catalog) {
  const list = getDepartmentCatalog(catalog);
  const headOfRoles = list.filter((d) => d.hasHeadOfRole).map(
    (d) => `Head of ${d.name}`
  );
  return [...headOfRoles, ...BOARD_POSITIONS];
}

/**
 * Format a position value for display in select options (e.g. "MAG - Main Chair" → "MAG: Main Chair").
 * @param {string} position
 * @returns {string}
 */
export function formatPositionOptionLabel(position) {
  if (position.startsWith("MAG - ")) {
    return `MAG: ${position.slice("MAG - ".length)}`;
  }
  if (position.startsWith("CF - ")) {
    return `CF: ${position.slice("CF - ".length)}`;
  }
  return position;
}
